import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent } from 'react';

/** 키보드(←/→) 1 스텝 이동량(px). */
const KEY_STEP = 16;

export interface PaneResizerProps {
  /**
   * 패널이 화면의 어느 쪽에 붙어 있는가.
   *  - 'left'  : 좌측 패널의 오른쪽 경계 → 오른쪽으로 끌수록 넓어진다.
   *  - 'right' : 우측 패널의 왼쪽 경계 → 왼쪽으로 끌수록 넓어진다.
   */
  anchor: 'left' | 'right';
  /** 현재 폭(px) — 드래그 시작 기준값이자 aria-valuenow. */
  width: number;
  min: number;
  max: number;
  /** 더블클릭 시 복귀할 기본 폭(px). */
  defaultWidth: number;
  /** 접근성 라벨(스크린리더 + title 툴팁). */
  label: string;
  /** 새 폭 요청 — clamp/영속화는 호출부(store)가 담당한다. */
  onResize: (width: number) => void;
  /** 루트 클래스 이름. 소비처별 CSS 스코프를 유지하기 위해 교체 가능. */
  baseClass?: string;
  /** `${baseClass}--${variant}` 수식 클래스(선택). */
  variant?: string;
}

/**
 * PaneResizer (design-20260724 §4.5 — CalendarResizer 승격) — 패널 경계 드래그 핸들 공용 컴포넌트.
 *
 * 마우스 드래그 + 키보드(←→ / Home / End) + 더블클릭 초기화를 제공한다.
 * 자기 자신은 폭 0 의 flex 아이템이고 히트 영역만 ::before 로 경계 위에 겹치므로,
 * 렌더되지 않는 경로에서는 레이아웃에 어떤 영향도 없다.
 *
 * 소비처: 캘린더 사이드바/레일(`cal-resizer`), 메모 패널(`memo-resizer`).
 * 클래스 이름을 baseClass 로 받는 이유 — 기존 소비처의 CSS 규칙을 그대로 두기 위함(무회귀).
 */
export function PaneResizer({
  anchor, width, min, max, defaultWidth, label, onResize,
  baseClass = 'pane-resizer', variant,
}: PaneResizerProps) {
  const [dragging, setDragging] = useState(false);
  // 드래그 중 언마운트(반응형 접힘 등) 시 window 리스너가 남지 않도록 해제 함수를 보관.
  const cleanupRef = useRef<(() => void) | null>(null);

  const dir = anchor === 'left' ? 1 : -1;

  useEffect(() => () => { cleanupRef.current?.(); }, []);

  const onMouseDown = useCallback((e: ReactMouseEvent) => {
    if (e.button !== 0) return; // 좌클릭만
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    // 드래그 중 텍스트 선택·커서 깜빡임 억제(전역 body 클래스).
    document.body.classList.add('cal-resizing');
    setDragging(true);

    const onMove = (ev: MouseEvent) => onResize(startW + dir * (ev.clientX - startX));
    const onUp = () => {
      document.body.classList.remove('cal-resizing');
      setDragging(false);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      cleanupRef.current = null;
    };
    cleanupRef.current = onUp;
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [dir, width, onResize]);

  // 키보드 — 화살표는 "구분선을 그 방향으로 민다"(좌/우 패널 어느 쪽이든 물리적으로 동일).
  const onKeyDown = useCallback((e: ReactKeyboardEvent) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); onResize(width - dir * KEY_STEP); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); onResize(width + dir * KEY_STEP); }
    else if (e.key === 'Home') { e.preventDefault(); onResize(min); }
    else if (e.key === 'End') { e.preventDefault(); onResize(max); }
  }, [dir, width, min, max, onResize]);

  const cls = [
    baseClass,
    variant ? `${baseClass}--${variant}` : null,
    dragging ? 'is-dragging' : null,
  ].filter(Boolean).join(' ');

  return (
    <div
      className={cls}
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={width}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      title={label}
      onMouseDown={onMouseDown}
      onKeyDown={onKeyDown}
      onDoubleClick={() => onResize(defaultWidth)}
    />
  );
}
